import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  SUPER_ADMIN_EMAIL,
  SUPER_ADMIN_UID,
  useAuth,
} from "../../auth/AuthProvider";
import PageHeader from "../../components/PageHeader";
import Modal from "../../components/Modal";
import { Banner, useToast } from "../../components/Feedback";

type AdminRow = {
  uid: string;
  email?: string;
  displayName?: string;
  note?: string;
  grantedBy?: string;
  grantedByEmail?: string;
  grantedAtMs?: number;
};

function formatDate(ms?: number) {
  if (!ms) return "—";
  return new Date(ms).toLocaleString("tr-TR");
}

/**
 * Yönetici yetkilerinin verildiği yer.
 *
 * Yetki bir custom claim değil, `admins/{uid}` belgesinin kendisi. Bu projede
 * claim yazılamadığı için (Cloud Run kimliksiz çağrıya kapalı, servis hesabı
 * setCustomUserClaims çağıramıyor) eski "Admin yap" düğmesi sessizce hiçbir
 * şey yapmıyordu. Belge yolunda yetki anında geçerli oluyor: karşı taraf
 * belgeyi canlı dinlediği için çıkıp girmesine bile gerek yok.
 */
export default function Roles() {
  const { user, isSuperAdmin } = useAuth();
  const [rows, setRows] = useState<AdminRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [input, setInput] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<AdminRow | null>(null);
  const { show, node: toast } = useToast();

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "admins"),
      (snap) => {
        setRows(
          snap.docs
            .map((d) => {
              const x = d.data();
              return {
                uid: d.id,
                email: x.email as string | undefined,
                displayName: x.displayName as string | undefined,
                note: x.note as string | undefined,
                grantedBy: x.grantedBy as string | undefined,
                grantedByEmail: x.grantedByEmail as string | undefined,
                grantedAtMs: x.grantedAt?.toMillis?.(),
              };
            })
            .sort((a, b) => (b.grantedAtMs ?? 0) - (a.grantedAtMs ?? 0))
        );
        setErr(null);
      },
      (e) => setErr(e.message)
    );
    return unsub;
  }, []);

  /**
   * Girilen değeri bir kullanıcıya bağlar.
   *
   * UID doğrudan; e-posta `users` koleksiyonunda aranıyor. E-postayı Auth'ta
   * aramak istemciden mümkün değil, o yüzden oyuncunun profil belgesi
   * olmalı — yani en az bir kez uygulamaya girmiş olmalı.
   */
  const resolveTarget = async (
    raw: string
  ): Promise<{ uid: string; email?: string; displayName?: string }> => {
    const v = raw.trim();
    if (!v) throw new Error("UID ya da e-posta gir.");

    if (v.includes("@")) {
      // Profil belgesindeki e-posta kayıt sırasında girildiği gibi
      // saklanıyor; Firebase Auth tarafı büyük/küçük harfe duyarsız ama
      // Firestore sorgusu değil. İki yazımı da deniyoruz.
      const lower = v.toLowerCase();
      const byEmail = async (value: string) =>
        getDocs(
          query(collection(db, "users"), where("email", "==", value), limit(2))
        );
      let snap = await byEmail(lower);
      if (snap.empty && v !== lower) snap = await byEmail(v);
      if (snap.empty) {
        throw new Error(
          `"${v}" ile kayıtlı oyuncu bulunamadı. Bu hesap uygulamaya en az bir kez girmiş olmalı — ya da UID'yi doğrudan yapıştır.`
        );
      }
      if (snap.size > 1) {
        throw new Error(
          "Bu e-posta ile birden fazla kayıt var. UID ile ekle."
        );
      }
      const d = snap.docs[0];
      return {
        uid: d.id,
        email: d.data().email as string | undefined,
        displayName: d.data().displayName as string | undefined,
      };
    }

    const profile = await getDoc(doc(db, "users", v));
    if (!profile.exists()) {
      // Profil yoksa da yetki verilebilsin: kural belgeye bakıyor, profile
      // değil. Sadece adı/e-postası boş görünür.
      return { uid: v };
    }
    return {
      uid: v,
      email: profile.data().email as string | undefined,
      displayName: profile.data().displayName as string | undefined,
    };
  };

  const grant = async () => {
    setBusy("add");
    setErr(null);
    try {
      const t = await resolveTarget(input);
      if (rows?.some((r) => r.uid === t.uid)) {
        throw new Error("Bu hesap zaten yönetici.");
      }
      await setDoc(doc(db, "admins", t.uid), {
        email: t.email ?? null,
        displayName: t.displayName ?? null,
        note: note.trim() || null,
        grantedBy: user?.uid ?? null,
        grantedByEmail: user?.email ?? null,
        grantedAt: serverTimestamp(),
      });
      setAdding(false);
      setInput("");
      setNote("");
      show(`${t.displayName ?? t.email ?? t.uid} artık yönetici.`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (r: AdminRow) => {
    setBusy(r.uid);
    setErr(null);
    try {
      await deleteDoc(doc(db, "admins", r.uid));
      show(`${r.displayName ?? r.email ?? r.uid} yöneticilikten çıkarıldı.`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
      setConfirmRemove(null);
    }
  };

  return (
    <div className="pb-24">
      <PageHeader
        title="Yetkiler"
        subtitle="Panele kimlerin girebileceği burada belirleniyor. Yetki anında geçerli olur, karşı tarafın çıkıp girmesi gerekmez."
        actions={
          isSuperAdmin ? (
            <button className="btn-primary text-sm" onClick={() => setAdding(true)}>
              + Yönetici ekle
            </button>
          ) : undefined
        }
      />

      {err && (
        <Banner tone="error" onClose={() => setErr(null)}>
          {err}
        </Banner>
      )}

      {!isSuperAdmin && (
        <Banner tone="info">
          Yetki verip alabilen tek hesap proje sahibi ({SUPER_ADMIN_EMAIL}).
          Listeyi görebilirsin ama değiştiremezsin.
        </Banner>
      )}

      <div className="card divide-y divide-stroke/70">
        {/* Proje sahibi kuralın içinde sabit — belgesi olmasa da yönetici. */}
        <RoleRow
          title="Proje sahibi"
          name={SUPER_ADMIN_EMAIL}
          uid={SUPER_ADMIN_UID}
          badge={
            <span className="pill bg-gold/20 text-gold border border-gold/40">
              SÜPER YÖNETİCİ
            </span>
          }
          meta="Yetkisi güvenlik kurallarına gömülü, buradan kaldırılamaz."
        />

        {rows === null && (
          <div className="p-6 text-sm text-textMuted">Yükleniyor…</div>
        )}

        {rows?.length === 0 && (
          <div className="p-8 text-center text-sm text-textMuted">
            Başka yönetici yok. Ekibe birini alacaksan yukarıdan ekle.
          </div>
        )}

        {rows?.map((r) => (
          <RoleRow
            key={r.uid}
            title={r.displayName ?? "—"}
            name={r.email ?? r.uid}
            uid={r.uid}
            badge={
              <span className="pill bg-primary/20 text-primarySoft border border-primary/40">
                YÖNETİCİ
              </span>
            }
            meta={[
              `Veren: ${r.grantedByEmail ?? r.grantedBy ?? "—"}`,
              formatDate(r.grantedAtMs),
              r.note,
            ]
              .filter(Boolean)
              .join(" · ")}
            action={
              isSuperAdmin ? (
                <button
                  className="btn-ghost text-sm text-danger"
                  disabled={busy !== null}
                  onClick={() => setConfirmRemove(r)}
                >
                  {busy === r.uid ? "…" : "Yetkiyi al"}
                </button>
              ) : undefined
            }
          />
        ))}
      </div>

      <Modal
        open={adding}
        title="Yönetici ekle"
        subtitle="E-posta ya da UID. E-posta ile eklemek için hesabın uygulamaya en az bir kez girmiş olması gerekiyor."
        onClose={() => setAdding(false)}
        footer={
          <>
            <button className="btn-ghost text-sm" onClick={() => setAdding(false)}>
              Vazgeç
            </button>
            <button
              className="btn-primary text-sm"
              disabled={busy !== null || !input.trim()}
              onClick={grant}
            >
              {busy === "add" ? "Veriliyor…" : "Yetki ver"}
            </button>
          </>
        }
      >
        <label className="section-label block mb-2">E-posta veya UID</label>
        <input
          className="input mb-4"
          placeholder="ornek@gmail.com"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && input.trim()) void grant();
          }}
        />
        <label className="section-label block mb-2">Not (isteğe bağlı)</label>
        <input
          className="input"
          placeholder="ör. içerik moderasyonu"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <p className="text-[11px] text-textMuted mt-4 leading-relaxed">
          Yönetici; oyuncuları yasaklayabilir, şikayetleri kapatabilir ve soru
          bankasını değiştirebilir. Yetki veremez — onu yalnızca proje sahibi
          yapar.
        </p>
      </Modal>

      <Modal
        open={confirmRemove !== null}
        title="Yetkiyi al"
        onClose={() => setConfirmRemove(null)}
        footer={
          <>
            <button
              className="btn-ghost text-sm"
              onClick={() => setConfirmRemove(null)}
            >
              Vazgeç
            </button>
            <button
              className="btn-danger text-sm"
              disabled={busy !== null}
              onClick={() => confirmRemove && revoke(confirmRemove)}
            >
              Yetkiyi al
            </button>
          </>
        }
      >
        <p className="text-sm text-textSecondary">
          <b>{confirmRemove?.displayName ?? confirmRemove?.email ?? confirmRemove?.uid}</b>{" "}
          panele artık giremeyecek. Bu, hesabı silmez veya yasaklamaz.
        </p>
      </Modal>

      {toast}
    </div>
  );
}

function RoleRow({
  title,
  name,
  uid,
  badge,
  meta,
  action,
}: {
  title: string;
  name: string;
  uid: string;
  badge: ReactNode;
  meta?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-4 p-4">
      <div className="h-10 w-10 rounded-full shrink-0 grid place-items-center font-extrabold text-white bg-gradient-to-br from-primary to-primaryDeep">
        {(title || name).charAt(0).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-extrabold truncate">{title}</span>
          {badge}
        </div>
        <div className="text-xs text-textMuted truncate">{name}</div>
        <Link
          to={`/admin/users/${uid}`}
          className="text-[11px] text-textMuted font-mono truncate hover:text-primarySoft"
        >
          {uid}
        </Link>
        {meta && (
          <div className="text-[11px] text-textMuted mt-0.5 truncate">{meta}</div>
        )}
      </div>
      {action}
    </div>
  );
}
